import { type Easing, motion } from "framer-motion"
import { useCallback, useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { TokenRoundelContent } from "./components/TokenRoundelContent"
import { tokenRoundelConfig } from "./data/token-roundel-data"
import { useResizeDetector } from "./hooks/use-resize-detector"
import { useTokenSectionObserver } from "./hooks/use-token-section-observer"

type Props = {
  /** Additional Tailwind classes */
  className?: string
  /** Render in place (no portal, no fixed positioning) */
  inline?: boolean
}

export function LandingTokenRoundel({ className = "", inline = false }: Props) {
  const [mounted, setMounted] = useState(false)
  const tokenInView = useTokenSectionObserver(mounted)
  const isResizing = useResizeDetector(inline ? 0 : tokenRoundelConfig.animation.resizeDebounce)

  useEffect(() => setMounted(true), [])

  const handleClick = useCallback(() => {
    const { scroll, responsive } = tokenRoundelConfig

    function tryScroll(): boolean {
      const el = document.getElementById("token")
      if (!el) return false

      const navEl = document.getElementById("landing-nav")
      const fallbackHeight =
        window.innerWidth < 768 ? responsive.fallbackNavHeight.mobile : responsive.fallbackNavHeight.desktop
      const navBottom = navEl?.getBoundingClientRect().bottom ?? fallbackHeight

      const targetY = el.getBoundingClientRect().top + window.pageYOffset - navBottom
      window.scrollTo({ top: targetY, behavior: scroll.behavior })

      if (window.history.replaceState) {
        window.history.replaceState(null, "", `${window.location.pathname}#token`)
      }
      return true
    }

    if (tryScroll()) return

    let attempts = 0
    const interval = setInterval(() => {
      attempts += 1
      if (tryScroll() || attempts > scroll.maxAttempts) {
        clearInterval(interval)
      }
    }, scroll.pollInterval)
  }, [])

  if (inline) {
    return (
      <div className={`relative flex items-center justify-center ${className}`.trim()}>
        <TokenRoundelContent inline={inline} />
      </div>
    )
  }

  if (!mounted || typeof document === "undefined") return null

  const portalEl = (
    <motion.button
      type="button"
      aria-label="Jump to $BRACKY section"
      onClick={handleClick}
      className={`cursor-pointer fixed bottom-24 2xl:bottom-26 right-20 xl:right-20 2xl:right-23 w-24 h-24 z-99999 hidden lg:flex items-center justify-center bg-transparent border-0 p-0 transform scale-[1.25] lg:scale-[1.8] xl:scale-[1.9] 2xl:scale-[2.05] ${className}`.trim()}
      animate={{ opacity: tokenInView || isResizing ? 0 : 1 }}
      transition={{
        duration: isResizing ? 0.1 : tokenRoundelConfig.animation.fadeTransition.duration,
        ease: tokenRoundelConfig.animation.fadeTransition.ease as Easing,
      }}
      style={{ pointerEvents: tokenInView || isResizing ? "none" : "auto" }}
    >
      <TokenRoundelContent inline={inline} />
    </motion.button>
  )

  return createPortal(portalEl, document.body)
}
